import { ColDef, GridOptions } from "@ag-grid-community/core";
import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { TP_ALL_SECTIONS } from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import { ModalController } from "@ionic/angular";
import { DoctorPanelActions } from "@modules/doctor-panel/store/doctor-panel.actions";
import { DoctorPanelState } from "@modules/doctor-panel/store/doctor-panel.state";
import { hasActionsExecuting } from "@ngxs-labs/actions-executing";
import { Select } from "@ngxs/store";
import { Icd9Patient } from "@trec/api";
import { C3TmplCellComponent, C3TmplCellOptions } from "@trec/components/table";
import { format } from "date-fns";
import { Observable } from "rxjs";
import { EditOperationDialogComponent } from "../../edit/edit-operation-dialog/edit-operation-dialog.component";

@Component({
  selector: "c3-config-operations",
  standalone: false,
  templateUrl: "./config-operations.component.html",
  styleUrl: "./config-operations.component.scss",
})
export class ConfigOperationsComponent implements OnInit, AfterViewInit {
  @ViewChild("actionsCellTmpl")
  actionsCellTmpl!: TemplateRef<unknown>;

  constructor(private modalCtrl: ModalController) {}

  ngOnInit() {
    this.icd9PatientList$.subscribe((list) => {
      this.filteredOperationsList = (list || []).filter(
        (item) => item.icd9SurgicalOperationCode && item.icd9SurgicalOperationDescription,
      );

      // Verifica se ci sono operazioni da aggiungere
      this.hasOperations = (list || []).some(
        (item) => !item.icd9SurgicalOperationCode || !item.icd9SurgicalOperationDescription,
      );
    });
  }

  @Select(hasActionsExecuting([DoctorPanelActions.FetchIcd9List]))
  loading$: Observable<boolean>;

  @Select(DoctorPanelState.icd9Patient)
  icd9PatientList$: Observable<Icd9Patient[]>;

  filteredOperationsList: Icd9Patient[] = [];
  hasOperations: boolean = false;

  TP_SECTIONS_INFO = TP_ALL_SECTIONS;

  readonly customAgGridOptions: GridOptions = {
    domLayout: "autoHeight",
    defaultColDef: {
      sortable: false,
      filter: false,
    },
    pagination: false,
    tooltipShowDelay: 500,
    enableBrowserTooltips: true,
  };
  columnDefs: ColDef[];

  async openEditDialog(surgicalOperation: Icd9Patient, action?: string) {
    const modal = await this.modalCtrl.create({
      component: EditOperationDialogComponent,
      componentProps: {
        context: surgicalOperation.context,
        icd9Code: surgicalOperation.icd9SurgicalOperationCode,
        selectedOperationDate: surgicalOperation.icd9SurgicalOperationDate,
        icd9Description: surgicalOperation.icd9SurgicalOperationDescription,
        deleteAction: action === "delete" ? true : false,
      },
      cssClass:
        action === "delete"
          ? "c3-modal-width-sm c3-modal-height-sm"
          : "c3-modal-width-sm c3-modal-height-lg",
    });
    await modal.present();
  }

  async openCreateDialog() {
    const modal = await this.modalCtrl.create({
      component: EditOperationDialogComponent,
      cssClass: "c3-modal-width-sm c3-modal-height-lg",
    });
    await modal.present();
  }

  ngAfterViewInit(): void {
    let contextInfo = JSON.parse(localStorage.getItem("CONTEXTS_INFO") || "{}");
    this.columnDefs = [
      {
        headerName: `Contesto`,
        colId: "icd9",
        flex: 1,
        minWidth: 120,
        valueGetter: (params) =>
          params.data?.context && contextInfo[params.data.context]
            ? contextInfo[params.data.context].label
            : "n/d",
      },
      {
        headerName: `Codice Icd-9`,
        colId: "icd9",
        flex: 1,
        minWidth: 70,
        valueGetter: (params) => params.data?.icd9SurgicalOperationCode ?? "n/d",
      },
      {
        headerName: `Descrizione Diagnosi`,
        colId: "icd9Description",
        flex: 3,
        minWidth: 200,
        valueGetter: (params) => params.data?.icd9SurgicalOperationDescription ?? "n/d",
        tooltipValueGetter: (params) => {
          // Mostra la descrizione completa nel tooltip
          return params.data?.icd9SurgicalOperationDescription ?? "Nessuna descrizione disponibile";
        },
      },
      {
        headerName: `Data`,
        colId: "date",
        flex: 1,
        minWidth: 80,
        valueGetter: (params) =>
          params.data.icd9SurgicalOperationDate
            ? format(new Date(params.data.icd9SurgicalOperationDate), "dd/MM/yyyy")
            : "n/d",
      },
      {
        headerName: `Azioni`,
        colId: "actions",
        width: 120,
        maxWidth: 120,
        suppressSizeToFit: true,
        cellRenderer: C3TmplCellComponent,
        cellRendererParams: { template: this.actionsCellTmpl } as C3TmplCellOptions,
      },
    ];
  }
}
